import { Box, Typography, Stack, alpha, useTheme, Button } from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { LuCopy, LuLayoutTemplate, LuRefreshCcw, LuFileText } from "react-icons/lu";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../authentication/AuthProvider";
import ColumnVisibilityButton from "../../../components/button/ColumnVisibilityButton";
import RefreshButton from "../../../components/button/RefreshButton";
import ColumnMenu from "../../../components/context-menu/ColumnMenu";
import ContextMenu from "../../../components/context-menu/ContextMenu";
import ExportAsXLSXButton from "../../../components/export/ExportAsXLSXButton";
import AgGrid from "../../../components/grid/AgGrid";
import { centerAlignColDef, defaultColDef, floatFormater, leftAlignColDef, rightAlignColDef } from "../../../components/grid/columnDefinitions/columnDefinitions";
import SearchBox from "../../../components/input/SearchBox";
import { useContextProvider } from "../../../components/page-context/ContextProvider";
import { Company } from "../../../db_access/company";
import { Product } from "../../../db_access/product";
import { ProductTemplateSharing } from "../../../db_access/product_template_sharing";
import { getOrgTokens } from "../../../theme/style/organizationPalette";

const ProductPreBuiltTemplateGrid = () => {
    const { t } = useTranslation();
    const { companyID } = useAuth();
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const tok = getOrgTokens(isDark);
    const { refresh, setRefresh, columnVisibilities, enqueueAlert } = useContextProvider();

    const gridRef = useRef(null);
    const gridHeaderContainerRef = useRef(null);
    const [products, setProducts] = useState([]);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [menuVisible, setMenuVisible] = useState(false);
    const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
    const [searchText, setSearchText] = useState("");
    const colVisibility_key = "product_prebuilt_template";
    const [columnMenuVisible, setColumnMenuVisible] = useState(false);
    const [companyName, setCompanyName] = useState("");

    const activeColor = '#3b82f6'; // Product Blue

    useEffect(() => {
        Company.get_public_info(companyID)
            .then((res) => res.json())
            .then((data) => setCompanyName(data?.[0]?.name || t("Organization")));
    }, [companyID, t]);

    const colDefs = useMemo(() => {
        const isHidden = (field) => !columnVisibilities[colVisibility_key]?.includes(field);
        return [
            { 
                headerName: t("Name"), 
                field: "name", 
                ...defaultColDef, 
                ...centerAlignColDef, 
                flex: 2,
                cellStyle: { ...tok.gridCellText, fontWeight: 500 } 
            },
            { headerName: t("Supplier"), field: "supplier", ...defaultColDef, ...centerAlignColDef, flex: 1 },
            { headerName: t("Year"), field: "year", ...defaultColDef, ...centerAlignColDef, width: 90 },
            { headerName: t("Type"), field: "type", ...defaultColDef, ...centerAlignColDef, width: 140 },
            { headerName: t("Category"), field: "category", ...defaultColDef, ...centerAlignColDef, width: 150 },
            { headerName: t("GWP (kg CO₂e)"), field: "gwp", ...defaultColDef, ...centerAlignColDef, ...floatFormater, width: 150 },
            { headerName: t("Date Modified"), field: "date_modified", ...defaultColDef, ...centerAlignColDef, width: 160 },
        ].map((colDef) => ({ 
            ...colDef, 
            hide: isHidden(colDef.field),
            headerClass: 'ag-header-cell-centered'
        }));
    }, [columnVisibilities, t, tok.gridCellText]);

    const defaultCols = useMemo(() => colDefs.map((col) => col.field), [colDefs]);

    useEffect(() => {
        ProductTemplateSharing.get_by_company_id(companyID)
            .then((res) => res.json())
            .then((data) => setProducts(data.map((item, i) => ({ ...item, id: i }))));
    }, [refresh, companyID]);

    const duplicateToMyWorkspace = useCallback(
        (selectedItems) => {
            const ids = selectedItems.map((s) => s.id);
            Product.duplicate(ids).then(() => {
                gridRef.current?.api?.deselectAll();
                enqueueAlert({ message: t("Templates duplicated to workspace"), severity: "success" });
                setRefresh((r) => !r);
            }).catch(() => enqueueAlert({ message: t("Failed to duplicate templates"), severity: "error" }));
        },
        [setRefresh, t, enqueueAlert]
    );

    const contextMenuOptions = useMemo(() => [
        { label: t("Duplicate to my workspace"), action: duplicateToMyWorkspace, needSelectedItems: true }
    ], [duplicateToMyWorkspace, t]);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            
            {/* Standardized Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Stack direction="row" spacing={2} alignItems="center">
                    <Box sx={{ 
                        width: 44, height: 44, borderRadius: tok.radius.button,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', 
                        bgcolor: alpha(activeColor, 0.1), color: activeColor 
                    }}>
                        <LuLayoutTemplate size={22} />
                    </Box>
                    <Box sx={{ textAlign: 'left' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: tok.text.primary, lineHeight: 1.2 }}>
                            {companyName} {t("Product Templates")}
                        </Typography>
                        <Typography sx={{ ...tok.gridCellText, color: tok.text.muted, fontSize: '0.75rem' }}>
                            {products.length} {t("templates available")}
                        </Typography>
                    </Box>
                </Stack>

                <Stack direction="row" spacing={1}>
                    {selectedProducts.length > 0 && (
                        <Button
                            variant="contained"
                            size="small"
                            startIcon={<LuCopy size={16} />}
                            onClick={() => duplicateToMyWorkspace(selectedProducts)}
                            sx={{
                                bgcolor: activeColor,
                                '&:hover': { bgcolor: alpha(activeColor, 0.9) },
                                borderRadius: tok.radius.button,
                                fontWeight: 700, textTransform: 'none', px: 2
                            }}
                        >
                            {t("Duplicate to Workspace")} ({selectedProducts.length})
                        </Button>
                    )}
                </Stack>
            </Box>

            {/* Standardized Toolbar */}
            <Stack direction="row" spacing={1.5} alignItems="center" ref={gridHeaderContainerRef} sx={{ mb: 0.5 }}>
                <Box sx={{ flex: 1 }}>
                    <SearchBox value={searchText} onChange={setSearchText} placeholder={t("Search templates...")} />
                </Box>
                <ExportAsXLSXButton gridRef={gridRef} filename="product_templates" />
                <ColumnVisibilityButton
                    columns={colDefs}
                    visibleColumns={columnVisibilities[colVisibility_key] || defaultCols}
                    columnVis_key={colVisibility_key}
                />
                <RefreshButton onRefresh={() => setRefresh((r) => !r)} />
            </Stack>

            <AgGrid
                rowData={products}
                columnDefs={colDefs}
                onSelectionChanged={(e) => setSelectedProducts(e.api.getSelectedRows())}
                handleContextMenu={(e) => {
                    e.preventDefault(); setMenuPosition({ x: e.pageX, y: e.pageY }); setMenuVisible(true);
                }}
                quickFilterText={searchText}
                ref={gridRef}
                domLayout="autoHeight"
            />

            <ColumnMenu
                anchorRef={gridHeaderContainerRef}
                visible={columnMenuVisible}
                onClose={() => setColumnMenuVisible(false)}
                colDefs={colDefs}
                colVisibility_key={colVisibility_key}
                defaultCols={defaultCols}
            />

            <ContextMenu x={menuPosition.x} y={menuPosition.y} visible={menuVisible} onClose={() => setMenuVisible(false)} selectedItems={selectedProducts} options={contextMenuOptions} />
        </Box>
    );
};

export default ProductPreBuiltTemplateGrid;
